import { Stack, Typography, useTheme } from "@mui/material";
import { CopyIcon, Input, RadioButtonGroup } from "components";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ParsedSamlMetadata } from "../create-sso";
import { XmlFileUpload } from "../create-sso/XmlFileUpload";
import { CopyAssertionAndAudienceUrl } from "./CopyAssertionAndAudienceUrl";

export const CopySamlUrl = ({
  assertionUrl,
  audienceUri,
}: {
  assertionUrl: string;
  audienceUri: string;
}) => {
  const theme = useTheme();
  const containerStyles = {
    padding: "16px 24px",
    border: `1px solid ${theme.palette.neutral["010"]}`,
    borderRadius: "8px",
    bgcolor: theme.palette.neutral.main,
  };

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [parsedXmlMetadataState, setParsedXmlMetadataState] = useState({
    certificateExpiry: "",
    issuer: "",
    signOnUrl: "",
  });

  const handleMetadataParsed = ({
    parsedMetadata,
    rawXmlContent,
  }: {
    parsedMetadata: ParsedSamlMetadata;
    rawXmlContent: string;
  }) => {
    setValue("metadata", rawXmlContent, { shouldDirty: true });
    setParsedXmlMetadataState({
      certificateExpiry: parsedMetadata.certificateExpiry || "",
      issuer: parsedMetadata.issuer || "",
      signOnUrl: parsedMetadata.signOnUrl || "",
    });
  };

  const metadataOptions = [
    {
      label: <Typography variant="h6">Add metadata URL manually</Typography>,
      value: "url",
      component: (
        <Stack gap="8px">
          <Input
            name="metadataUrl"
            label="Metadata URL"
            placeholder="Enter App Federation Metadata URL"
            control={control}
            sx={{ mt: "8px", width: "100%" }}
            errorMessage={errors?.metadataUrl?.message?.toString() || ""}
            error={errors?.metadataUrl ? true : false}
          />
          <Typography
            variant="body-xsmall"
            color={theme.palette.lightBg.low}
            width="95%"
          >
            Open your Enterprise Application in Azure → go to Single sign-on →
            SAML Certificates → copy the App Federation Metadata URL and paste
            it here.
          </Typography>
        </Stack>
      ),
    },
    {
      label: <Typography variant="h6">Federation metadata XML file</Typography>,
      value: "xml",
      component: (
        <Stack mt="8px" gap="12px">
          <XmlFileUpload onMetadataParsed={handleMetadataParsed} />
          {errors?.metadata && (
            <Typography variant="body-small" color={theme.palette.error.main}>
              {errors?.metadata?.message?.toString() || ""}
            </Typography>
          )}
          {parsedXmlMetadataState?.signOnUrl &&
            parsedXmlMetadataState?.issuer &&
            parsedXmlMetadataState?.certificateExpiry && (
              <Stack
                borderRadius="8px"
                border="1px solid #D9EDFF"
                p="16px"
                gap="16px"
                bgcolor="#E8F3FE"
              >
                <Input
                  name="sign_on_url"
                  label="Sign on URL"
                  value={parsedXmlMetadataState.signOnUrl}
                  disabled={true}
                  InputProps={{
                    endAdornment: (
                      <CopyIcon
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                        onClick={() => {}}
                      />
                    ),
                  }}
                />
                <Input
                  name="issuer"
                  label="Issuer"
                  value={parsedXmlMetadataState.signOnUrl}
                  disabled={true}
                  InputProps={{
                    endAdornment: (
                      <CopyIcon
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                        onClick={() => {}}
                      />
                    ),
                  }}
                />
                <Stack gap="2px">
                  <Typography variant="subtitle1">
                    Signing certificate
                  </Typography>
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                  >
                    Expiring Friday, June 25, 2027
                  </Typography>
                </Stack>
              </Stack>
            )}
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    if (getValues("metadataType") === "unselected") {
      setValue("metadataType", "url");
    }
  }, []);

  return (
    <Stack gap="12px">
      <CopyAssertionAndAudienceUrl
        assertionUrl={assertionUrl}
        audienceUri={audienceUri}
      />
      <Stack
        sx={{
          gap: "24px",
          ...containerStyles,
        }}
      >
        <Stack>
          <Typography variant="h6">Upload the metadata</Typography>
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Configure the metadata copied from the identity provider
          </Typography>
        </Stack>
        <RadioButtonGroup
          name="metadataType"
          control={control}
          data={metadataOptions}
          labelMappingKey="label"
          valueMappingKey="value"
          defaultValue="url"
        />
      </Stack>
    </Stack>
  );
};
